/**
 * Findings lane — the copilot findings registry (admin triage), the dev worker
 * plane (status + deploy jobs) and the payment-classifier rules (client-lane
 * self-heal). One typed client for the pinned contract:
 *
 *   GET    /api/admin/worker/status              (admin session)
 *   POST   /api/admin/worker/deploy-jobs         (admin session)
 *   GET    /api/admin/findings[?status&category] (admin session)
 *   PATCH  /api/admin/findings/{id}/priority     (admin session)
 *   PATCH  /api/admin/findings/{id}/status       (admin session)
 *   PATCH  /api/admin/findings/{id}/auto-deploy  (admin session)
 *   GET    /api/admin/findings/{id}/export       (admin session, text/markdown)
 *   GET/POST/DELETE /api/analytics/payment-classifier-rules (workspace)
 *
 * The worker token routes (heartbeat, findings/next, result) are NOT here —
 * they belong to the worker process, never to the browser.
 */
import { api, BASE_URL, getToken } from './client';

// ── Copilot findings registry (admin triage) ─────────────────────────────────

/**
 * Live commander progress for a picked-up finding. The worker POSTs the log
 * tail to /api/admin/worker/findings/{workspaceId}/{findingId}/progress; the
 * admin list injects the newest entry from the in-memory store (no persistence).
 */
export interface FindingProgressDto {
  /** Last 15 commander-log lines, clamped to 2000 chars server-side. */
  logTail: string;
  updatedAtUtc: string;
}

/** Mirrors FindingsController.CopilotFindingDto (camelCase JSON). */
export interface CopilotFindingDto {
  id: number;
  workspaceId: string;
  workspaceName: string | null;
  /** WHO registered it (a5suite username) — the per-person scope. */
  createdBy: string | null;
  title: string;
  description: string;
  category: 'ClientPattern' | 'ProductBug' | string;
  severity: string;
  refsJson: string;
  suggestedFix: string;
  /** The user's own notes on their ticket (user lane; null = never written). */
  notes: string | null;
  status: string;
  /**
   * Server-persisted auto-deploy mark (finding 14): when every marked finding
   * is Fixed and the system is idle, the BACKEND queues the deploy job and
   * flips the marked findings to Done once it succeeds.
   */
  autoDeploy: boolean;
  /** Lower = more urgent; 1000 = not yet triaged. 1–9999. */
  priority: number;
  sourceDecisionId: number | null;
  commitHash: string | null;
  createdAtUtc: string;
  updatedAtUtc: string | null;
  /** Worker progress for PickedUp/InProgress rows; null until the first report. */
  progress?: FindingProgressDto | null;
}

// ── User lane: MY tickets (the active workspace's findings) ────────────────

/** GET /api/findings — the signed-in user's own workspace tickets. */
export const findingsApi = {
  list: (params: { status?: string; category?: string } = {}) =>
    api.get<CopilotFindingDto[]>('/findings', params),

  /** Save the user's notes on their ticket (empty string clears). */
  updateNotes: (id: number, notes: string) =>
    api.patch<CopilotFindingDto>(`/findings/${id}/notes`, { notes }),
};

// ── Dev worker plane ─────────────────────────────────────────────────────────

/**
 * One commit on HEAD the worker has NOT deployed yet (git log line, newest
 * first, max 15). A leading synthetic entry with hash:'' means the worker
 * has no lastDeployedHash state yet ("nežinoma paskutinio diegimo versija").
 */
export interface UndeployedCommitDto {
  hash: string;
  subject: string;
}

export interface WorkerHeartbeatDto {
  lastSeenUtc: string | null;
  version: string | null;
  connected: boolean;
  /**
   * Undeployed commits since lastDeployedHash (max 15, newest first; []
   * when HEAD == lastDeployedHash). Whatever the worker last sent — an
   * older worker that never reports it leaves this undefined.
   */
  undeployed?: UndeployedCommitDto[] | null;
    scriptStale?: boolean;
}

export interface DeployJobDto {
  id: number;
  description: string;
  status: string;
  createdAtUtc: string;
  finishedAtUtc: string | null;
  outputTail: string;
  /** HEAD hash the job built, reported by the worker on job completion. */
  commitHash?: string | null;
}

export interface WorkerStatusDto {
  heartbeat: WorkerHeartbeatDto;
  /** The last 10 deploy jobs, newest first. */
  deployJobs: DeployJobDto[];
  /**
   * Server-owned auto-deploy mode (finding 15): All = every Fixed
   * ProductBug/Enhancement finding ships when the system is idle (then flips
   * to Done, or to Failed with the deploy error in its progress tail);
   * Marked = the finding 14 opt-in marks; Off = no automation. Undefined on
   * an older API = Marked (the pre-finding-15 behavior).
   */
  autoDeployMode?: 'All' | 'Marked' | 'Off';
}

/** A job that still occupies the single deploy slot (only one allowed at a time). */
export function isDeployJobActive(job: DeployJobDto): boolean {
  return job.status === 'Queued' || job.status === 'Running';
}

// ── Payment classifier rules (client-lane self-heal) ─────────────────────────

export type ClassifierMatchField = 'SenderContains' | 'DocumentPrefix';

/** Mirrors the PaymentClassifierRule entity (workspace-scoped, MatchRule precedent). */
export interface PaymentClassifierRuleDto {
  id: number;
  workspaceId: string;
  matchField: ClassifierMatchField | string;
  matchValue: string;
  note: string | null;
  createdBy: string | null;
  createdAtUtc: string;
}

export const findingsAdminApi = {
  /** Findings across ALL workspaces, priority asc (most urgent first). */
  list: (params: { status?: string; category?: string } = {}) =>
    api.get<CopilotFindingDto[]>('/admin/findings', params),

  /** Triage: set queue priority (1–9999, lower = more urgent). */
  setPriority: (id: number, priority: number) =>
    api.patch<CopilotFindingDto>(`/admin/findings/${id}/priority`, { priority }),

  /** Lifecycle move (Registered/Queued/PickedUp/InProgress/Fixed/Done/Rejected). */
  setStatus: (id: number, status: string) =>
    api.patch<CopilotFindingDto>(`/admin/findings/${id}/status`, { status }),

  /** Arm/disarm the server-side auto-deploy watch on one finding (finding 14). */
  setAutoDeploy: (id: number, autoDeploy: boolean) =>
    api.patch<CopilotFindingDto>(`/admin/findings/${id}/auto-deploy`, { autoDeploy }),

  /**
   * Export one finding as commander-review markdown and download it as a blob
   * (the api client parses JSON — this endpoint returns text/markdown).
   */
  export: async (id: number): Promise<void> => {
    const headers: Record<string, string> = { Accept: 'text/markdown' };
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${BASE_URL}/admin/findings/${id}/export`, {
      headers,
      credentials: 'include',
    });
    if (!response.ok) throw new Error(`Export failed: ${response.status}`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = `finding-${id}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  },
};

export const workerApi = {
  /** Worker heartbeat + recent deploy jobs (admin session). */
  status: () => api.get<WorkerStatusDto>('/admin/worker/status'),

  /** Queue a deploy job for the worker. 409 while one is queued/running. */
  createDeployJob: (description?: string) =>
    api.post<DeployJobDto>('/admin/worker/deploy-jobs', description ? { description } : {}),
};

export const classifierRulesApi = {
  list: () =>
    api.get<PaymentClassifierRuleDto[]>('/analytics/payment-classifier-rules'),

  /** matchField must be SenderContains|DocumentPrefix; matchValue 1–64 chars. */
  create: (body: { matchField: string; matchValue: string; note?: string }) =>
    api.post<PaymentClassifierRuleDto>('/analytics/payment-classifier-rules', body),

  remove: (id: number) =>
    api.delete(`/analytics/payment-classifier-rules/${id}`),
};
